import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import AuthImage from "./AuthImage";
import { X } from "lucide-react";

export default function PinnedImagesModal({ currentIds = [], onClose, onSaved }) {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(currentIds.slice(0, 5));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/images?limit=120").then(r => setImages(r.data || []));
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) setSelected(selected.filter(x => x !== id));
    else if (selected.length < 5) setSelected([...selected, id]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/profile", { pinned_image_ids: selected });
      onSaved && onSaved();
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-stretch sm:items-center sm:p-6 overflow-y-auto" data-testid="pinned-modal">
      <div className="w-full max-w-4xl mx-auto bg-[var(--noir-elev)] border border-white/10 rounded-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="font-fraunces italic text-2xl text-[var(--cream)]">{t("pinned_modal.title")}</div>
            <div className="font-meta mt-1">{t("pinned_modal.sub", { count: selected.length })}</div>
          </div>
          <button onClick={onClose} className="text-[var(--cream-soft)] hover:text-[var(--magenta)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">
          {images.length === 0 ? (
            <div className="font-fraunces italic text-[var(--cream-soft)] py-10 text-center">
              {t("pinned_modal.no_images")}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
              {images.map((img) => {
                const idx = selected.indexOf(img.id);
                const isSel = idx >= 0;
                return (
                  <button key={img.id} onClick={() => toggle(img.id)}
                    className={`relative aspect-square overflow-hidden rounded-sm border ${isSel ? "border-[var(--magenta)] ring-2 ring-[var(--magenta)]" : "border-white/10 hover:border-white/40"}`}
                    data-testid={`pin-${img.id}`}>
                    <AuthImage imageId={img.id} className="w-full h-full object-cover" />
                    {isSel && (
                      <div className="absolute top-1 left-1 h-5 w-5 rounded-full bg-[var(--magenta)] text-black text-[10px] font-bold flex items-center justify-center">
                        {idx + 1}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/10">
          <button onClick={onClose} className="btn-ghost">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving} className="btn-magenta" data-testid="pin-save">
            {saving ? t("pinned_modal.saving") : t("pinned_modal.save_selection")}
          </button>
        </div>
      </div>
    </div>
  );
}
